import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SubscriptionGuard from "@/components/SubscriptionGuard";
import HelpButton from "@/components/HelpButton";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DashboardPage from "@/pages/DashboardPage";
import LeadDetailPage from "@/pages/LeadDetailPage";
import ChatWidgetDemo from "@/pages/ChatWidgetDemo";
import EmbedPage from "@/pages/EmbedPage";
import IntegrationsPage from "@/pages/IntegrationsPage";
import BrandingPage from "@/pages/BrandingPage";
import TeamPage from "@/pages/TeamPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import JoinTeamPage from "@/pages/JoinTeamPage";
import QuestionsPage from "@/pages/QuestionsPage";
import SetupWizardPage from "@/pages/SetupWizardPage";
import NotificationsPage from "@/pages/NotificationsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import SubscriptionManagePage from "@/pages/SubscriptionManagePage";
import CMSIntegrationsPage from "@/pages/CMSIntegrationsPage";
import RetainerSettingsPage from "@/pages/RetainerSettingsPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminSystemHealthPage from "@/pages/AdminSystemHealthPage";
import HelpCenterPage from "@/pages/HelpCenterPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsOfServicePage from "@/pages/TermsOfServicePage";
import UnsubscribePage from "@/pages/UnsubscribePage";
import OptInEvidencePage from "@/pages/OptInEvidencePage";
import SMSSignupPage from "@/pages/SMSSignupPage";
import PricingPage from "@/pages/PricingPage";
import ForITTeamsPage from "@/pages/ForITTeamsPage";
import NotFoundPage from "@/pages/NotFoundPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full spinner" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected route that also requires active subscription
const PaidRoute = ({ children }) => {
  return (
    <ProtectedRoute>
      <SubscriptionGuard>
        {children}
      </SubscriptionGuard>
    </ProtectedRoute>
  );
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/chat-demo" element={<ChatWidgetDemo />} />
      <Route path="/join" element={<JoinTeamPage />} />
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsOfServicePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/for-it-teams" element={<ForITTeamsPage />} />
      <Route path="/unsubscribe" element={<UnsubscribePage />} />
      <Route path="/opt-in-evidence" element={<OptInEvidencePage />} />
      <Route path="/sms-signup" element={<SMSSignupPage />} />
      
      {/* Subscription page - accessible without subscription */}
      <Route
        path="/subscription"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/success"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/cancel"
        element={
          <ProtectedRoute>
            <SubscriptionPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription/manage"
        element={
          <PaidRoute>
            <SubscriptionManagePage />
          </PaidRoute>
        }
      />
      
      {/* Setup wizard - accessible without subscription (onboarding) */}
      <Route
        path="/setup"
        element={
          <ProtectedRoute>
            <SetupWizardPage />
          </ProtectedRoute>
        }
      />
      
      {/* Premium features - require active subscription */}
      <Route
        path="/dashboard"
        element={
          <PaidRoute>
            <DashboardPage />
          </PaidRoute>
        }
      />
      <Route
        path="/leads/:id"
        element={
          <PaidRoute>
            <LeadDetailPage />
          </PaidRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <PaidRoute>
            <IntegrationsPage />
          </PaidRoute>
        }
      />
      <Route
        path="/integrations/case-management"
        element={
          <PaidRoute>
            <CMSIntegrationsPage />
          </PaidRoute>
        }
      />
      <Route
        path="/branding"
        element={
          <PaidRoute>
            <BrandingPage />
          </PaidRoute>
        }
      />
      <Route
        path="/questions"
        element={
          <PaidRoute>
            <QuestionsPage />
          </PaidRoute>
        }
      />
      <Route
        path="/retainer-settings"
        element={
          <PaidRoute>
            <RetainerSettingsPage />
          </PaidRoute>
        }
      />
      <Route
        path="/team"
        element={
          <PaidRoute>
            <TeamPage />
          </PaidRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <PaidRoute>
            <AnalyticsPage />
          </PaidRoute>
        }
      />
      <Route
        path="/embed"
        element={
          <PaidRoute>
            <EmbedPage />
          </PaidRoute>
        }
      />
      <Route
        path="/notifications"
        element={
          <PaidRoute>
            <NotificationsPage />
          </PaidRoute>
        }
      />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
      <Route path="/admin/users/:userId" element={<AdminDashboardPage />} />
      <Route path="/admin/system-health" element={<AdminSystemHealthPage />} />
      
      {/* Help Center - accessible to all logged-in users */}
      <Route
        path="/help"
        element={
          <ProtectedRoute>
            <HelpCenterPage />
          </ProtectedRoute>
        }
      />

      {/* 404 Not Found - Catch all unmatched routes */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <HelpButtonWrapper />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

// Only show HelpButton on authenticated pages (not landing, login, register)
function HelpButtonWrapper() {
  const { user } = useAuth();
  const isPublicPage = ['/login', '/register', '/forgot-password', '/reset-password', '/chat-demo', '/join', '/'].includes(window.location.pathname) || window.location.pathname.startsWith('/admin');
  
  if (!user || isPublicPage) return null;
  return <HelpButton />;
}

export default App;
